import { isUpdatingResponse } from '../api/retryPolicy'

/**
 * This page's build id, from its own `<meta name="arvoke-build">`
 * (frontend/config/buildId.ts), the same one `/version.json` holds while this
 * build is live. Null in development, where there's no build to compare.
 */
export const CURRENT_BUILD_ID: string | null =
  typeof document === 'undefined'
    ? null
    : (document.querySelector<HTMLMetaElement>('meta[name="arvoke-build"]')?.content ?? null)

/** How often the updating screen asks whether the update is over. */
export const UPDATING_POLL_INTERVAL_MS = 10_000

/** The fewest milliseconds between two checks for a newer build. */
export const NEW_BUILD_CHECK_THROTTLE_MS = 30_000

/**
 * The API's unauthenticated health check, beside its versioned routes:
 * `/api/v1` gives `/api/health/control-db`.
 */
export function updatingHealthCheckUrl(apiBaseUrl: string): string {
  return `${apiBaseUrl.replace(/\/+$/, '').replace(/\/v1$/, '')}/health/control-db`
}

type Fetch = typeof fetch

/**
 * Whether the API still says it's updating: `'updating'` for its updating
 * 503, `'unavailable'` while nothing answers (no connection, or the proxy's
 * gateway errors as containers restart), `'answered'` for anything else. Only
 * an answer ends the wait: reloading into a server that isn't there would
 * trade this screen for an error.
 */
export async function checkUpdating(
  url: string,
  fetchImpl: Fetch,
  signal?: AbortSignal
): Promise<'updating' | 'unavailable' | 'answered'> {
  let response: Response
  try {
    // A health check, not app data, so not a query (no cache, no retries).
    response = await fetchImpl(url, { cache: 'no-store', signal })
  } catch {
    return 'unavailable'
  }
  if (response.status === 503) {
    let body: unknown = null
    try {
      body = await response.json()
    } catch {
      // Not the proxy's JSON: a 503 that isn't the updating one.
    }
    return isUpdatingResponse(response.status, body) ? 'updating' : 'unavailable'
  }
  if (response.status === 502 || response.status === 504) return 'unavailable'
  return 'answered'
}

/**
 * Asks every `intervalMs` whether the update is over and calls `onDone` the
 * first time the API answers. It never gives up while the API says it's
 * updating. Returns a function that stops it.
 */
export function pollUntilUpdated({
  url,
  onDone,
  fetchImpl = (...args) => fetch(...args),
  intervalMs = UPDATING_POLL_INTERVAL_MS,
}: {
  url: string
  onDone: () => void
  fetchImpl?: Fetch
  intervalMs?: number
}): () => void {
  let stopped = false
  let timer: ReturnType<typeof setTimeout> | undefined
  let inFlight: AbortController | undefined

  const tick = async () => {
    inFlight = new AbortController()
    // A check that hangs gives way to the next one instead of stalling the wait.
    const timeout = setTimeout(() => inFlight?.abort(), intervalMs)
    const result = await checkUpdating(url, fetchImpl, inFlight.signal)
    clearTimeout(timeout)
    if (stopped) return
    if (result === 'answered') {
      stopped = true
      onDone()
      return
    }
    timer = setTimeout(() => void tick(), intervalMs)
  }

  timer = setTimeout(() => void tick(), intervalMs)
  return () => {
    stopped = true
    clearTimeout(timer)
    inFlight?.abort()
  }
}

/**
 * Checks for a newer build: fetches `/version.json` and calls `onNewBuild`
 * when its build differs from `currentBuild`. At most once every
 * `throttleMs`, counted from when the checker was made (a page that just
 * loaded is the newest build). Without a current build (development), it
 * does nothing. A failed check is ignored; the next one tries again.
 */
export function createNewBuildCheck({
  currentBuild,
  onNewBuild,
  versionUrl = `${import.meta.env.BASE_URL}version.json`,
  fetchImpl = (...args) => fetch(...args),
  throttleMs = NEW_BUILD_CHECK_THROTTLE_MS,
  now = () => Date.now(),
}: {
  currentBuild: string | null
  onNewBuild: () => void
  versionUrl?: string
  fetchImpl?: Fetch
  throttleMs?: number
  now?: () => number
}): () => Promise<void> {
  let lastCheckAt = now()
  return async () => {
    if (!currentBuild) return
    const at = now()
    if (at - lastCheckAt < throttleMs) return
    lastCheckAt = at
    try {
      const response = await fetchImpl(versionUrl, { cache: 'no-store' })
      if (!response.ok) return
      const { build } = (await response.json()) as { build?: unknown }
      if (typeof build === 'string' && build && build !== currentBuild) onNewBuild()
    } catch {
      // Offline, or an old server without version.json: try again next time.
    }
  }
}

/** How often an open tab asks whether an update is coming. */
export const UPDATE_STATUS_POLL_INTERVAL_MS = 20_000

/**
 * The update notice the proxy answers itself (so it answers while the API is
 * frozen too): `/api/v1` gives `/api/update-status`. The contract is in
 * backend/devops/app_lifecycle/operations/update_status.py.
 */
export function updateStatusUrl(apiBaseUrl: string): string {
  return `${apiBaseUrl.replace(/\/+$/, '').replace(/\/v1$/, '')}/update-status`
}

/** An update as an open tab knows it; times are epoch milliseconds. */
export type UpdateStatus =
  | { state: 'none' }
  | { state: 'upcoming'; startsAt: number; downtimeSeconds: number }
  | { state: 'updating'; estimatedEndAt: number | null }

function epochMs(value: unknown): number | null {
  if (typeof value !== 'string') return null
  const ms = Date.parse(value)
  return Number.isFinite(ms) ? ms : null
}

/**
 * Reads an update document: the notice's, or an updating 503's body. A
 * postponed update, or anything unreadable, is no update.
 */
export function parseUpdateStatus(body: unknown): UpdateStatus {
  let data = body
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data)
    } catch {
      return { state: 'none' }
    }
  }
  if (!data || typeof data !== 'object') return { state: 'none' }
  const doc = data as Record<string, unknown>
  if (doc.state === 'updating' || doc.code === 'updating') {
    return { state: 'updating', estimatedEndAt: epochMs(doc.estimatedEndAt) }
  }
  if (doc.state === 'upcoming') {
    const startsAt = epochMs(doc.startsAt)
    const downtime = doc.estimatedDowntimeSeconds
    if (startsAt === null || typeof downtime !== 'number' || !(downtime > 0))
      return { state: 'none' }
    return { state: 'upcoming', startsAt, downtimeSeconds: downtime }
  }
  return { state: 'none' }
}

/** Asks the proxy for the update notice; a failure or a server without one is no update. */
export async function fetchUpdateStatus(
  url: string,
  fetchImpl: Fetch,
  signal?: AbortSignal
): Promise<UpdateStatus> {
  try {
    const response = await fetchImpl(url, { cache: 'no-store', signal })
    if (!response.ok) return { state: 'none' }
    return parseUpdateStatus(await response.json())
  } catch {
    return { state: 'none' }
  }
}

/**
 * A duration as update notices give it: `48s`, `1m`, `1m 23s`. Seconds round
 * up, so a countdown never shows less time than is left.
 */
export function formatUpdateDuration(seconds: number): string {
  const total = Math.max(1, Math.ceil(seconds))
  const minutes = Math.floor(total / 60)
  const rest = total % 60
  if (minutes === 0) return `${rest}s`
  return rest === 0 ? `${minutes}m` : `${minutes}m ${rest}s`
}

/** Seconds from `now` until `at`, or null once it has gone by (then: "any moment"). */
export function secondsUntil(at: number, now: number): number | null {
  const seconds = (at - now) / 1000
  return seconds >= 1 ? seconds : null
}
